import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { useId } from 'react';
import styled from 'styled-components';

import type { ModelInfo } from '@paradigm/terminal-core';

import { groupModelsByProvider } from '../../models/provider-groups';

interface Props {
  readonly models: readonly ModelInfo[];
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
}

export default function ModelPicker({ models, selectedId, onSelect }: Props) {
  const menuId = useId();
  if (models.length === 0) return null;
  const selected = models.find((m) => m.id === selectedId) ?? models[0];
  if (selected == null) return null;
  const groups = groupModelsByProvider(models);
  return (
    <DropdownMenuPrimitive.Root>
      <Trigger aria-label={`Model: ${selected.label}`}>
        <TriggerLabel>{selected.label}</TriggerLabel>
      </Trigger>
      <DropdownMenuPrimitive.Portal>
        <Content sideOffset={6} align="end" side="top">
          {groups.map((group, i) => {
            // Radix DropdownMenu.Group doesn't auto-associate a label (unlike
            // Select.Group), so wire it up explicitly for screen readers.
            const headingId = `${menuId}-group-${i}`;
            return (
              <Group key={group.label} aria-labelledby={headingId}>
                <Heading id={headingId}>{group.label}</Heading>
                {group.models.map((m) => (
                  <Item key={m.id} onSelect={() => onSelect(m.id)}>
                    <ItemLabel>
                      {m.label}
                      {m.legacy === true && <LegacyTag>Legacy</LegacyTag>}
                    </ItemLabel>
                    <CheckSlot>
                      {m.id === selected.id && <CheckIcon />}
                    </CheckSlot>
                  </Item>
                ))}
              </Group>
            );
          })}
        </Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
      <path
        d="M5 12L10 17L20 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const Trigger = styled(DropdownMenuPrimitive.Trigger)`
  all: unset;
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--text-label);
  cursor: pointer;

  &:hover,
  &[data-state='open'] {
    color: var(--text-primary);
    background-color: var(--hover-bg);
  }
`;

const TriggerLabel = styled.span`
  white-space: nowrap;
`;

const Content = styled(DropdownMenuPrimitive.Content)`
  min-width: 180px;
  padding: 6px;
  border: 1px solid var(--border-color);
  background-color: var(--bg-color-container);
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.32);
  z-index: 2000;
`;

const Group = styled(DropdownMenuPrimitive.Group)`
  & + & {
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid var(--border-color);
  }
`;

const Heading = styled(DropdownMenuPrimitive.Label)`
  padding: 6px 8px 4px;
  font-size: 0.7rem;
  color: var(--text-label);
`;

const Item = styled(DropdownMenuPrimitive.Item)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 16px;
  align-items: center;
  gap: 8px;
  height: 28px;
  padding: 0 8px 0 16px;
  border-radius: 4px;
  font-size: 0.8rem;
  color: var(--text-primary);
  cursor: pointer;
  outline: none;
  user-select: none;

  &[data-highlighted],
  &:hover {
    background-color: var(--hover-bg);
  }
`;

const ItemLabel = styled.span`
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LegacyTag = styled.span`
  font-size: 0.7rem;
  color: var(--text-label);
`;

const CheckSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
`;
